/**
 * Beat 13, *It Fails* — `docs/beat-13-plan.md`.
 *
 * Five stages and every one ends on a clock and ONLY on a clock: there is no
 * condition the player can meet, fail or stall (the plan: *"nothing here asks
 * the player to do anything they could fail at"*). The no-input run reaches
 * `end` in `IT_FAILS_TOTAL` seconds, and `beat-13-fails.spec.ts` holds every
 * stage-entry tick to that, with and without every key held.
 *
 * Each stage's `enter` emits its cue; the room stages the yard from the cue
 * (`src/world/yardFailsStaging.ts`) and the part frames it.
 */
import type { Beat, Stage } from '@/story/types';

export const IT_FAILS_CUES = {
  open: 'it-fails.open',
  jam: 'it-fails.jam',
  whistle: 'it-fails.whistle',
  bell: 'it-fails.bell',
  surge: 'it-fails.surge',
  outside: 'it-fails.outside',
  fadeOut: 'it-fails.fadeOut',
} as const;

/** The plan's table, in seconds. The surge is a 6 s shot (D-095). */
export const IT_FAILS_SECONDS = { jam: 10, whistle: 8, bell: 12, surge: 6, outside: 6 } as const;

/** Black held after the hold, before `end` hands over. Beat 11's and 12's value. */
export const IT_FAILS_CLOSE_HOLD = 2.4;

export const IT_FAILS_TOTAL = Object.values(IT_FAILS_SECONDS).reduce((sum, seconds) => sum + seconds, 0) + IT_FAILS_CLOSE_HOLD;

function timed(id: string, seconds: number, cue: string): Stage {
  return { id, enter: (_ctx, emit): void => emit(cue), done: (ctx): boolean => ctx.stageTime >= seconds };
}

export const IT_FAILS_BEAT: Beat = {
  number: 13,
  id: 'act3.itFails',
  stages: [
    {
      id: 'the-jam',
      enter: (_ctx, emit): void => { emit(IT_FAILS_CUES.open); emit(IT_FAILS_CUES.jam); },
      done: (ctx): boolean => ctx.stageTime >= IT_FAILS_SECONDS.jam,
    },
    timed('the-whistle', IT_FAILS_SECONDS.whistle, IT_FAILS_CUES.whistle),
    timed('the-bell', IT_FAILS_SECONDS.bell, IT_FAILS_CUES.bell),
    timed('the-surge', IT_FAILS_SECONDS.surge, IT_FAILS_CUES.surge),
    // The hold owns its whole six seconds: fading on entry would take the
    // last two bells away before the player could place them in the yard.
    timed('outside', IT_FAILS_SECONDS.outside, IT_FAILS_CUES.outside),
    {
      id: 'close',
      enter: (_ctx, emit): void => emit(IT_FAILS_CUES.fadeOut),
      done: (ctx): boolean => ctx.stageTime >= IT_FAILS_CLOSE_HOLD,
    },
  ],
};
